import { useCallback, useEffect, useRef, useState } from "react";

/**
 * In-app PWA install popup for dash.gestock.tech.
 *
 * Chrome no longer shows an automatic install prompt: it only fires
 * `beforeinstallprompt` (once the manifest, HTTPS, service worker and the
 * engagement heuristics are satisfied) and the app must render its own popup.
 * This component:
 *
 *   1. Captures `beforeinstallprompt`, then shows a branded French popup once
 *      (re-dismissals are remembered for 30 days).
 *   2. Runs the native prompt when the user taps "Installer".
 *   3. On iOS (where the event never fires) shows the Safari
 *      "Partager ▸ Sur l'écran d'accueil" instructions instead.
 *   4. Hides itself permanently once the app is installed.
 */

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

const DISMISS_KEY = "gestock.pwa.install.dismissedAt";
const DISMISS_DAYS = 30;
const POPUP_DELAY_MS = 2500;

function wasRecentlyDismissed(): boolean {
  try {
    const raw = window.localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const dismissedAt = Number(raw);
    if (!dismissedAt) return false;
    return Date.now() - dismissedAt < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

function rememberDismissal(): void {
  try {
    window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    /* private mode: dismissal just won't persist */
  }
}

function isIosSafari(): boolean {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  const standalone = (navigator as unknown as { standalone?: boolean }).standalone;
  if (standalone) return false; // already installed from the home screen
  // iPad on modern iPadOS reports as Mac; the touch query catches it.
  const isIpad = /Macintosh/.test(navigator.userAgent) && "ontouchend" in document;
  return isIpad || /iPad|iPhone|iPod/.test(navigator.userAgent);
}

export default function PwaInstallPrompt() {
  const deferredPrompt = useRef<BeforeInstallPromptEvent | null>(null);
  const [promptReady, setPromptReady] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [visible, setVisible] = useState(false);
  const [installing, setInstalling] = useState(false);

  const isIos = isIosSafari();
  const blocked = wasRecentlyDismissed();

  useEffect(() => {
    const onBeforeInstallPrompt = (event: Event) => {
      // Suppress Chrome's mini-infobar: we render our own popup instead.
      event.preventDefault();
      deferredPrompt.current = event as BeforeInstallPromptEvent;
      if (!blocked) setPromptReady(true);
    };

    const onAppInstalled = () => {
      setInstalled(true);
      setVisible(false);
      deferredPrompt.current = null;
      setPromptReady(false);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, [blocked]);

  // Decide once: Chromium popup when the event arrives, iOS hint otherwise.
  const shouldOffer = !installed && !blocked && (promptReady || (isIos && !installed));

  useEffect(() => {
    if (!shouldOffer || visible) return;
    const timer = window.setTimeout(() => setVisible(true), POPUP_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [shouldOffer, visible]);

  const install = useCallback(async () => {
    const promptEvent = deferredPrompt.current;
    if (!promptEvent) return;

    setInstalling(true);
    setVisible(false);
    try {
      await promptEvent.prompt();
      const { outcome } = await promptEvent.userChoice;
      if (outcome === "accepted") {
        setInstalled(true);
      } else {
        rememberDismissal();
      }
    } catch {
      rememberDismissal();
    } finally {
      deferredPrompt.current = null;
      setPromptReady(false);
      setInstalling(false);
    }
  }, []);

  const dismiss = useCallback(() => {
    setVisible(false);
    rememberDismissal();
  }, []);

  if (installed || installing || !visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Installer l'application GESTOCK"
      className="fixed bottom-4 left-1/2 z-[200] w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 animate-fadeIn rounded-2xl border border-blue-200 bg-white p-4 shadow-[0_18px_40px_rgba(15,23,42,0.18)]"
    >
      <div className="flex items-start gap-3">
        <img
          src={`${process.env.PUBLIC_URL || ""}/logo192.png`}
          alt=""
          aria-hidden
          className="h-11 w-11 shrink-0 rounded-xl"
        />
        <div className="min-w-0 flex-1">
          <p className="m-0 text-sm font-bold text-slate-900">
            Installer GESTOCK sur votre appareil
          </p>
          <p className="mb-0 mt-1 text-xs leading-relaxed text-slate-500">
            {isIos
              ? "Sur iPhone / iPad : touchez Partager ⬆ puis « Sur l'écran d'accueil »."
              : "Accédez à votre stock en un clic, même hors ligne, comme une vraie application."}
          </p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Fermer"
          className="-mr-1 -mt-1 shrink-0 rounded-lg p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
        >
          ✕
        </button>
      </div>

      {!isIos ? (
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={install}
            className="flex-1 rounded-xl bg-[#2563EB] px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
          >
            Installer
          </button>
          <button
            type="button"
            onClick={dismiss}
            className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
          >
            Plus tard
          </button>
        </div>
      ) : (
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={dismiss}
            className="flex-1 rounded-xl bg-[#2563EB] px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
          >
            J'ai compris
          </button>
        </div>
      )}
    </div>
  );
}
